import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Cookie } from 'lucide-react';
import { Button } from '../ui/button';
import {
  isRegulatedRegion,
  needsCookieDecision,
  setCookieConsent,
  subscribeToCookiePreferencesOpen,
} from '../../utils/cookieConsent';

const CookieConsentBanner = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setIsOpen(needsCookieDecision() && isRegulatedRegion());
    return subscribeToCookiePreferencesOpen(() => setIsOpen(true));
  }, []);

  const handleChoice = (analyticsAllowed) => {
    setCookieConsent(analyticsAllowed);
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-x-0 bottom-0 z-[1090] px-3 pb-24 sm:pb-6 pointer-events-none"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          role="dialog"
          aria-modal="false"
          aria-labelledby="cookie-consent-title"
        >
          <div className="pointer-events-auto mx-auto max-w-3xl rounded-2xl border border-border bg-white shadow-2xl p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Cookie className="w-5 h-5 text-primary" aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <h2
                  id="cookie-consent-title"
                  className="text-sm sm:text-base font-bold text-foreground"
                >
                  {t('cookie_consent_title', 'We value your privacy')}
                </h2>
                <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  {t(
                    'cookie_consent_body',
                    'We use essential cookies to keep you signed in and remember your preferences. With your permission, we also use analytics cookies to understand how the app is used. You can change your choice anytime from the footer.'
                  )}{' '}
                  <Link
                    to="/privacy"
                    className="underline hover:text-foreground"
                    onClick={() => setIsOpen(false)}
                  >
                    {t('privacy_policy', 'Privacy Policy')}
                  </Link>
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-3">
              <Button
                variant="outline"
                onClick={() => handleChoice(false)}
                className="h-10 sm:w-auto"
              >
                {t('cookie_reject', 'Reject non-essential')}
              </Button>
              <Button
                onClick={() => handleChoice(true)}
                className="h-10 sm:w-auto"
              >
                {t('cookie_accept', 'Accept all')}
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsentBanner;
